import AdvisorBackendService from 'AdvisorBackendService';
import BackendService from 'BackendService';
import { CommonLoggedInDataContext } from 'CommonLoggedInData';
import React from 'react';
import InvestorContext from 'InvestorContext';
import ContentManagementInterface from '../models/ContentManagementInterface';
import { withContexts } from '../context/ContextWrapper';
import { getBackendService as resolveBackendService } from './fundOpportunityEOI/backendService';
import { buildInitialState } from './fundOpportunityEOI/buildInitialState';
import * as contentAndPdfHandlers from './fundOpportunityEOI/contentAndPdfHandlers';
import * as contentEditingHandlers from './fundOpportunityEOI/contentEditingHandlers';
import { FundOpportunityEOIContentPanel } from './fundOpportunityEOI/FundOpportunityEOIContentPanel';
import { FundOpportunityEOIPageView } from './fundOpportunityEOI/FundOpportunityEOIPageView';
import * as lifecycleHandlers from './fundOpportunityEOI/lifecycleHandlers';
import * as uiHandlers from './fundOpportunityEOI/uiHandlers';
import { FundOpportunityProps, FundOpportunityState } from './fundOpportunityEOI/types';

export class FundOpportunityEOI extends React.Component<
  FundOpportunityProps,
  FundOpportunityState
> {
  static sendMessageIconRef = React.createRef<HTMLDivElement>();

  constructor(props: FundOpportunityProps) {
    super(props);
    this.state = buildInitialState(props, props.print, props.printDraft);
  }

  getBackendService(): BackendService | AdvisorBackendService {
    return resolveBackendService(this);
  }

  loadStaticPdfAvailability(): void {
    contentAndPdfHandlers.loadStaticPdfAvailability(this);
  }

  refreshInvestorPreservingScroll(): void {
    contentAndPdfHandlers.refreshInvestorPreservingScroll(this);
  }

  getScrollStorageKey(): string | null {
    return contentAndPdfHandlers.getScrollStorageKey(this);
  }

  resetModalFlags(): Partial<FundOpportunityState> {
    return contentAndPdfHandlers.resetModalFlags(this);
  }

  hasSubmittedEOI(): boolean {
    return contentAndPdfHandlers.hasSubmittedEOI(this);
  }

  setFilter(filter: string): void {
    contentAndPdfHandlers.setFilter(this, filter);
  }

  generateHTML(): void {
    contentAndPdfHandlers.generateHTML(this);
  }

  currentStatus() {
    return contentAndPdfHandlers.currentStatus(this);
  }

  downloadApplication(): void {
    contentAndPdfHandlers.downloadApplication(this);
  }

  downloadPDF(draft: boolean = false, callback?: () => void): void {
    contentAndPdfHandlers.downloadPDF(this, draft, callback);
  }

  checkPDFJob(): void {
    contentAndPdfHandlers.checkPDFJob(this);
  }

  initiateUpdateEOI(): void {
    contentAndPdfHandlers.initiateUpdateEOI(this);
  }

  initiateWithdrawEOI(): void {
    contentAndPdfHandlers.initiateWithdrawEOI(this);
  }

  cancelUpdateEOI(): void {
    contentAndPdfHandlers.cancelUpdateEOI(this);
  }

  componentDidMount(): void {
    lifecycleHandlers.componentDidMount(this);
  }

  componentDidUpdate(
    prevProps: Readonly<FundOpportunityProps>,
    prevState: Readonly<FundOpportunityState>,
  ): void {
    lifecycleHandlers.componentDidUpdate(this, prevProps, prevState);
  }

  loadContent(): Promise<void> {
    return lifecycleHandlers.loadContent(this);
  }

  waitForCarouselImages(): void {
    lifecycleHandlers.waitForCarouselImages(this);
  }

  dataRead(template: string): Promise<void> {
    return contentEditingHandlers.dataRead(this, template);
  }

  submitEoi(investment: number, completedCallback?: () => void): void {
    contentEditingHandlers.submitEoi(this, investment, completedCallback);
  }

  commitEOIWithdrawal(completedCallback?: () => void): void {
    contentEditingHandlers.commitEOIWithdrawal(this, completedCallback);
  }

  suggestEOI(investment: number, comment?: string, completedCallback?: () => void): void {
    contentEditingHandlers.suggestEOI(this, investment, comment, completedCallback);
  }

  acceptSuggestedEOI(completedCallback?: () => void): void {
    contentEditingHandlers.acceptSuggestedEOI(this, completedCallback);
  }

  rejectSuggestedEOI(completedCallback?: () => void): void {
    contentEditingHandlers.rejectSuggestedEOI(this, completedCallback);
  }

  withdrawSuggestedEOI(completedCallback?: () => void): void {
    contentEditingHandlers.withdrawSuggestedEOI(this, completedCallback);
  }

  acceptInvitation(data: Parameters<typeof contentEditingHandlers.acceptInvitation>[1]): void {
    contentEditingHandlers.acceptInvitation(this, data);
  }

  rejectInvitation(): void {
    contentEditingHandlers.rejectInvitation(this);
  }

  showLightboxBackground(): void {
    uiHandlers.showLightboxBackground(this);
  }

  closeLightbox(): void {
    uiHandlers.closeLightbox(this);
  }

  sendMessage(): void {
    uiHandlers.sendMessage(this);
  }

  share(): void {
    uiHandlers.share(this);
  }

  cancelShare(): void {
    uiHandlers.cancelShare(this);
  }

  createAppointment(): void {
    uiHandlers.createAppointment(this);
  }

  submitMessage(message: string): void {
    uiHandlers.submitMessage(this, message);
  }

  handleConfirmEOI(e: React.MouseEvent<HTMLInputElement, MouseEvent>): void {
    uiHandlers.handleConfirmEOI(this, e);
  }

  validateIndicativeInvestment(value?: string): void {
    uiHandlers.validateIndicativeInvestment(this, value);
  }

  renderContent(contentManagementInterface: ContentManagementInterface): JSX.Element {
    return (
      <FundOpportunityEOIContentPanel
        instance={this}
        contentManagementInterface={contentManagementInterface}
      />
    );
  }

  applicationRelevant(): boolean {
    return uiHandlers.applicationRelevant(this);
  }

  canRenderApplication(): boolean {
    return uiHandlers.canRenderApplication(this);
  }

  renderApplication(): React.ReactNode {
    return uiHandlers.renderApplication(this);
  }

  render(): JSX.Element {
    return <FundOpportunityEOIPageView instance={this} />;
  }
}

export default withContexts(FundOpportunityEOI, {
  investorContext: InvestorContext,
  userContext: CommonLoggedInDataContext,
});
